import { useState, useEffect } from 'react';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Modal, Button, Input, InputNumber, Table, message, Tag, Card, Drawer, Tabs, Select, Switch } from 'antd';
import { saveRoleList, getRoleList, getRoleLog, getCompanyList, saveCompanyList } from "../services/userService.js";
import styles from "./RoleConfig.module.css";
import logIcon from '../assets/log.svg';
const RoleConfig = ({
  visible,
  title = "Quota Configuration",
  onCancel,
  afterClose
}) => {
  const [isLogCenterOpen, setIsLogCenterOpen] = useState(false);
  const [logPagination, setLogPagination] = useState({
    current: 1,
    pageSize: 10
  });
  const [roleLog, setRoleLog] = useState("");
  const [companyLog, setCompanyLog] = useState("");
  const [logData, setLogData] = useState({ rows: [], total: 0 });
  const [inputValues, setInputValues] = useState({}); // 每行输入框值
  const [roles, setRoles] = useState([]);
  const [tabFlag, setTabFlag] = useState(1);
  const [rolesSelect, setRolesSelect] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [loading, setLoading] = useState(false);

  const onChange = key => {
    setTabFlag(key);
  };

  // 组件挂载时从后端获取角色数据
  useEffect(() => {
    if (visible) {
      getRoleList()
        .then(response => {
          const listRole = response.rows.map(role => ({
            ...role, 
            key: role.roleId, 
            roleName: role.roleName, 
            tokenCount: role.tokenCount,
            notes: role.notes
          }))
          setRoles(listRole);

          const roleList = Object.entries(listRole).map(([, value]) => ({
            value: value.roleId,
            label: `${value.roleName} [${value.tokenCount}]`,
          }));
          setRolesSelect(roleList);
        });

      getCompanyList()
        .then(response => {
          setCompanyList(response.rows.map(company => ({
            ...company, 
            id: company.id, 
            companyName: company.companyName, 
            roleId: company.roleId,
            totalDailyQuota: company.totalDailyQuota,
            domainRule: company.domainRule,
            partnerName: company.partnerName,
          })));
        });
    }
  }, [visible]);

  const logColumns = [
    {
      title: "Role",
      dataIndex: ["roleMap", "role"],
      key: "rplerMap_role",
      width: 200,
      render: (_, record) => record.roleMap?.role || "-"
    },
    {
      title: "Operation",
      dataIndex: ["roleMap", "operation"],
      key: "roleMap_operation",
      width: 200,
      render: (_, record) => record.roleMap?.operation || "-"
    },
    {
      title: "Operator",
      dataIndex: ["roleMap", "operName"],
      key: "roleMap_operName",
      width: 200,
      render: (_, record) => record.roleMap?.operName || "-"
    },
    {
      title: "Operation Time",
      dataIndex: ["roleMap", "operTime"],
      key: "roleMap_operTime",
      width: 200,
      render: (_, record) => {
        const operTimeStr = record.roleMap?.operTime;
        if (!operTimeStr) return "-";
        const utcString = operTimeStr.replace(" ", "T") + "Z";
        const date = new Date(utcString);
        return date.toLocaleString();
      }
    },
    {
      title: "Result",
      dataIndex: ["roleMap", "result"],
      key: "roleMap_result",
      width: 200,
      render: (_, record) => {
        const result = record.roleMap?.result || "-";
        const color = result == "Success" ? "green" :
          result == "Failure" ? "red" : "inherit";
        return <span style={{ color }}>{result}</span>;
      }
    },
  ];

  const CompanyColumns = [
    {
      title: 'CompanyName',
      dataIndex: 'companyName',
      editable: true,
      width: 100,
      render: (text, record) =>
        <Input
          defaultValue={text}
          onBlur={e => {
            const value = e.target.value.trim();
            if (value && value !== text) {
              handleEdit(record.id, 'companyName', value);
            }
          }}
        />
    },
    {
      title: 'Domain Rule',
      dataIndex: 'domainRule',
      editable: true,
      width: 250,
      render: (_, record) => (
        <Card
          size="small"
          className={styles.ruleColumnCard}
          styles={{ body: { padding: 4 } }}
        >
          {record.domainRule.map((tag) => (
            <Tag
              key={tag}
              closable
              onClose={() => handleClose(tag, record)}
            >
              {tag}
            </Tag>
          ))}
          <Input
            value={inputValues[record.id] || ''}
            onChange={(e) => handleInputChange(e, record.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleInputConfirm(record);
              }
            }}
            placeholder="Enter and press enter to add"
          />
        </Card>
      ),
    },
    {
      title: 'Partner',
      dataIndex: 'partnerName',
      editable: true,
      width: 250,
    },
    {
      title: 'Total Daily Quota',
      dataIndex: 'totalDailyQuota',
      editable: true,
      width: 150,
      render: (text, record) => (
        <InputNumber
          defaultValue={text} // 初始值用 defaultValue，避免受控频繁更新
          min={1}
          onBlur={e => {
            const value = e.target.value ? Number(e.target.value) : text;
            handleEdit(record.id, 'totalDailyQuota', value);
          }}
        />
      ),
    },
    {
      title: 'Quota Role',
      dataIndex: 'roleId',
      editable: true,
      width: 200,
      render: (text, record) => (
        <Select
          style={{ width: '100%' }}
          options={rolesSelect}
          defaultValue={text}
          onChange={(value) => {
            handleEdit(record.id, 'roleId', value);
          }}
        />
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      width: 50,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => handleDelete(record.id)}
          icon={<DeleteOutlined />}
        ></Button>
      ),
    },
  ];

  const columns = [
    {
      title: 'Role',
      dataIndex: 'roleName',
      editable: true,
      render: (text, record) =>
        record.isNew ? (
          <Input
            defaultValue={text}
            onBlur={e => {
              const value = e.target.value.trim();
              if (value && value !== text) {
                handleEdit(record.key, 'roleName', value);
              }
            }}
          />
        ) : (
          text
        ),
    },
    {
      title: 'Daily Limit',
      dataIndex: 'tokenCount',
      editable: true,
      render: (text, record) => (
        <InputNumber
          defaultValue={text} // 初始值用 defaultValue，避免受控频繁更新
          min={1}
          onBlur={e => {
            const value = e.target.value ? Number(e.target.value) : text;
            handleEdit(record.key, 'tokenCount', value);
          }}
        />
      ),
    },
    {
      title: 'Multi-Image Upload',
      dataIndex: 'multiImageTag',
      editable: true,
      render: (text, record) => (
        <Switch 
          checked={text === 1}
          checkedChildren="Open"
          unCheckedChildren="Close"
          disabled={record.roleName === 'Freemium'}
          style={{
            backgroundColor: text === 1 ? '#16796f' : '#535f5eb6',
          }}
          onChange={(checked) => {
            const value = checked ? 1 : 0;
            handleEdit(record.key, 'multiImageTag', value);
          }}
        />
      ),
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      editable: true,
      render: (text, record) => (
        <Input
          defaultValue={text}
          placeholder="Please enter ..."
          onBlur={(e) => {
            const value = e.target.value.trim();
            if (value && value !== text) {
                handleEdit(record.key, 'notes', value);
              }
            if (!value) {
              message.error('Notes is a required field!');
            }
          }}
        />
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (_, record) => (
        <Button
          type="link"
          disabled={record.roleName === 'Freemium'} // 禁用普通角色的删除按钮
          onClick={() => handleDelete(record.key)}
          style={{
            color: record.roleName === 'Freemium' ? '#d9d9d9' : undefined, // 改变普通角色按钮的颜色
          }}
          icon={<DeleteOutlined />}
        ></Button>
      ),
    },
  ];

  const closeDrawer = () => {
    setIsLogCenterOpen(false)
    setLogPagination({ current: 1, pageSize: 10 });
  }

  const handleAdd = () => {
    const newKey = Date.now();
    if(1 === tabFlag){
      setCompanyList([
        ...companyList,
        { id: newKey, companyName: '', roleId: '', isNew: true, domainRule: [], totalDailyQuota: 0 }
      ]);
    }else{
      setRoles([
        ...roles,
        { key: newKey, roleName: '', RoleCount: 0, isNew: true, rules: [], multiImageTag: 0 }
      ]);
    }
  };

  const handleEdit = (key, field, value) => {
    if(1 === tabFlag){//Company
      const targetRole = companyList.find(item => item.id === key);
      if (!targetRole) return;

      const oldValue = targetRole[field];
      const newValue = value;

      const newCompany = companyList.map(item => {
        if (item.id === key) {
          return { ...item, [field]: value };
        }
        return item;
      });

      setCompanyList(newCompany);
      let log = ""
      if (!targetRole.isNew && oldValue !== newValue) {
        log = `Company "${targetRole.companyName}" is ${field} Field modified: old value ${oldValue}，new value ${newValue}.`
        setCompanyLog(prevLog => prevLog + log + "\n");
      }
    } else {//Role
      const targetRole = roles.find(item => item.key === key);
      if (!targetRole) return;

      const oldValue = targetRole[field];
      const newValue = value;

      const newRoles = roles.map(item => {
        if (item.key === key) {
          return { ...item, [field]: value };
        }
        return item;
      });

      setRoles(newRoles);
      let log = ""
      if (!targetRole.isNew && oldValue !== newValue) {
        log = `Role "${targetRole.roleName}" is ${field} Field modified: old value ${oldValue}，new value ${newValue}.`
        setRoleLog(prevLog => prevLog + log + "\n");
      }
    }
  };

  const handleInputChange = (e, recordKey) => {
    setInputValues({ ...inputValues, [recordKey]: e.target.value });
  };

  const handleInputConfirm = (record) => {
    const inputValue = (inputValues[record.id] || '').trim();

    if (!inputValue) return;

    // Email suffix format verification
    const emailRegex = /^[@a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(inputValue)) {
      message.error('Please enter a valid email suffix (e.g., example.com)');
      return;
    }

    // 当前角色重复检查
    const currentRole = companyList.find(r => r.id === record.id);
    if (currentRole.domainRule.includes(inputValue)) {
      message.error('The rule already exists for the current role');
      return;
    }

    // 全局重复检查
    const isDuplicate = companyList.some(role =>
      role.domainRule.includes(inputValue)
    );

    if (isDuplicate) {
      message.error('The rule already exists in another role');
      return;
    }

    // 更新状态
    const newData = companyList.map(item =>
      item.id === record.id
        ? { ...item, domainRule: [...item.domainRule, inputValue] }
        : item
    );
    setCompanyList(newData);
    setInputValues({ ...inputValues, [record.id]: '' });
    if (!record.isNew) {
      const log = `Company "${record.companyName}" is ${inputValue} rule added.`;
      setCompanyLog(prevLog => prevLog + log + "\n");
    }
  };
  const fetchLog = async (pagination) => {
    try {
      onCancel()
      setIsLogCenterOpen(true);
      const response = await getRoleLog({
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
      });
      setLogData({
        rows: response.rows || [],
        total: response.total || 0,
      });
    } catch (error) {
      console.error('Fetch log error:', error);
    }
  }
  const handleClose = (tag, record) => {
    const currentRole = companyList.find(item => item.id === record.id);
    if (!currentRole) return;
    const newData = companyList.map((item) => {
      if (item.id === record.id) {
        return {
          ...item,
          domainRule: item.domainRule.filter((t) => t !== tag),
        };
      }
      return item;
    });
    setCompanyList(newData);
    if (!currentRole.isNew) {
      const log = `Company "${currentRole.companyName}" is ${tag} rule deleted.`;
      setCompanyLog(prevLog => prevLog + log + "\n");
    }
  };

  const handleDelete = (key) => {

    if(1 === tabFlag){
      const deletedRoles = companyList.filter(role => role.id === key && !role.isNew);
      if (deletedRoles != null) {
        let log = ""
        deletedRoles.forEach(role => {
          log += `Company "${role.companyName}" is deleted.`;
        });
        setCompanyLog(prevLog => prevLog + log + "\n");
      }
      const newRoles = companyList.filter(role => role.id !== key);
      setCompanyList(newRoles);
    }else{
      const deletedRoles = roles.filter(role => role.key === key && !role.isNew);
      if (deletedRoles != null) {
        let log = ""
        deletedRoles.forEach(role => {
          log += `Role "${role.roleName}" is deleted.`;
        });
        setRoleLog(prevLog => prevLog + log + "\n");
      }
      const newRoles = roles.filter(role => role.key !== key);
      setRoles(newRoles);
    }
  };

  const handleSaveRoleOk = () => {
    if (loading) return;
    setLoading(true);
    if(1 === tabFlag){
      companySave();
    }else{
      roleSave();
    }
    setLoading(false);
  }

  const companySave = () => {
    const hasEmptyNewRole = companyList.some(role =>
      role.isNew && (!role.companyName || role.domainRule.length === 0 || role.totalDailyQuota === 0 || !role.roleId)
    );

    if (hasEmptyNewRole) {
      message.error('Please enter all corresponding content!');
      return;
    }

    const newRoles = companyList.filter(role => role.isNew);
    let log = ""
    if (newRoles != null) {
      newRoles.forEach(role => {
        log += `Company "${role.companyName}" is created.`;
      });
    }
    const role = {
      companyLog: companyLog + log,
      company: companyList,
    }

    //向后端发送数据
    saveCompanyList(role)
      .then(response => {
        if (response && response.code == 200) {
          message.success('Success');
        } else {
          message.error(response.msg);
        }
      })
      .catch(() => {
        message.error('Fail');
      })
      .finally(() => {
        onCancel()
      });
    setCompanyLog("")
  }

  const roleSave = () => {
    const hasEmptyNewRole = roles.some(role =>
      role.isNew && (!role.roleName || !role.tokenCount)
    );

    if (hasEmptyNewRole) {
      message.error('Please enter the name of the new role and the daily limit');
      return;
    }
    const newRoles = roles.filter(role => role.isNew);
    let log = ""
    if (newRoles != null) {
      newRoles.forEach(role => {
        log += `Role "${role.roleName}" is created.`;
      });
    }
    const role = {
      roleLog: roleLog + log,
      role: roles,
    }
    //向后端发送数据
    saveRoleList(role)
      .then(response => {
        if (response && response.code == 200) {
          message.success('Success');
        } else {
          message.error(response.msg);
        }
      })
      .catch(() => {
        message.error('Fail');
      })
      .finally(() => {
        onCancel()
      });
    setRoleLog("")
  }

  const handleLogTableChange = (logPagination) => {
    setLogPagination({
      current: logPagination.current,
      pageSize: logPagination.pageSize,
    });
    fetchLog({
      current: logPagination.current,
      pageSize: logPagination.pageSize,
    });
  }

  const items = [
    {
      key: 1,
      label: 'Company',
      children: (
        <>
          <Table
            columns={CompanyColumns}
            dataSource={companyList}
            rowKey="id"
            pagination={false}
          />
        </>
      ),
    },
    {
      key: 2,
      label: 'Quota Role',
      children: (
        <Table
          columns={columns}
          dataSource={roles}
          rowClassName={() => "editable-row"}
          pagination={false}
        />
      ),
    },
  ];

  return (
    <>
      <Modal
        width={1000}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span>{title}</span>
            <Button
              icon={
                <img
                  src={logIcon}
                  alt="log"
                  style={{ width: 30, height: 30 }}
                />
              }
              onClick={() => fetchLog(logPagination)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                border: "none",
                background: "none",
                boxShadow: "none",
              }}
            />
          </div>
        }
        open={visible}
        onOk={handleSaveRoleOk}
        okButtonProps={{ loading, disabled: loading }}
        onCancel={onCancel}
        afterClose={afterClose}
        footer={[
          <div key="footer" className={styles.footerDiv}>
            <Button key="back" onClick={handleAdd} icon={<PlusOutlined />}>
              Create
            </Button>
            <Button key="submit" type="primary" onClick={handleSaveRoleOk} loading={loading}>
              Save
            </Button>
          </div>
        ]}
      >
        <Tabs defaultActiveKey="1" items={items} onChange={onChange} />
        
      </Modal>

      <Drawer
        placement="right"
        width={"80%"}
        onClose={closeDrawer}
        open={isLogCenterOpen}
      >
        <Table
          rowKey="operId"
          columns={logColumns}
          dataSource={logData.rows}
          pagination={{
            total: logData.total,
            current: logPagination.current,
            pageSize: logPagination.pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `${total} in total`,
          }}
          scroll={{ x: "max-content" }}
          onChange={handleLogTableChange}
        />
      </Drawer>
    </>
  );
};

export default RoleConfig;
